import { useQuery } from '@tanstack/react-query';
import { fetchCourseSummary, fetchCourseEngagement } from "../api";

/**
 * Hook to fetch course summary data for a specific user
 * @param {string|null} userId - The ID of the user to fetch course summary for
 * @returns {Object} React Query result object
 * @property {Object} data - The course summary data if available
 * @property {boolean} isLoading - Whether the data is currently loading
 * @property {boolean} isError - Whether an error occurred
 * @property {Object} error - Error object if an error occurred
 * @property {Function} refetch - Function to manually refetch the data
 */
export function useCourseSummary(userId) {
  return useQuery({
    queryKey: ['course-summary', userId],
    queryFn: () => fetchCourseSummary(userId),
  },
    {
      enabled: userId !== null,
    });
}

/**
 * Hook to fetch course engagement data for a specific user within a date range
 * @param {string|null} userId - The ID of the user to fetch course engagement for
 * @param {string} startDate - Start date for the engagement period in ISO format
 * @param {string} endDate - End date for the engagement period in ISO format
 * @returns {Object} React Query result object
 * @property {Object} data - The course engagement data if available
 * @property {boolean} isLoading - Whether the data is currently loading
 * @property {boolean} isError - Whether an error occurred
 * @property {Object} error - Error object if an error occurred
 * @property {Function} refetch - Function to manually refetch the data
 */
export function useCourseEngagement(userId, startDate, endDate) {
  return useQuery({
    queryKey: ['course-engagement', userId, startDate, endDate],
    queryFn: () => fetchCourseEngagement(userId, startDate, endDate),
  },
    {
      enabled: (userId !== null) && startDate && endDate && startDate !== endDate,
    });
} 